import { type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { _isEmpty } from '@/littledash';
import { web as webRoute } from '@/support/route';
import { agentUrlSlugMap } from '@/components/Agents/AgentUtils';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import { NoDataMessage } from '@/components/NoDataCard/NoDataCard';
import type { AgentSessionSummaryV1 } from '@/model/Agent.model';
import styles from './RecentSessionsPanel.module.scss';

interface RecentSessionsPanelProps {
  sessions: Array<AgentSessionSummaryV1>;
  hasError: boolean;
  onViewAllSessions: () => void;
}

/**
 * Compact quick-glance rail on the AI Agents hub showing the user's most recent
 * agent sessions. Each row links to its session page. Distinct from the full
 * sessions table on the Sessions tab. Data is fetched by the parent AgentsPage
 * so the cards and this rail load together behind a single page spinner.
 */
const RecentSessionsPanel: FC<RecentSessionsPanelProps> = ({ sessions, hasError, onViewAllSessions }) => {
  const navigate = useNavigate();

  const openSession = (session: AgentSessionSummaryV1): void => {
    navigate(
      webRoute('agents.session', {
        slug: agentUrlSlugMap[session.agent_type],
        sessionApiId: session.api_id,
      })
    );
  };

  const renderBody = () => {
    if (hasError) {
      return (
        <NoDataMessage
          title="Couldn't load recent sessions"
          text="Something went wrong. Try refreshing the page."
          dark
        />
      );
    }

    if (_isEmpty(sessions)) {
      return <NoDataMessage title="No recent sessions" text="Agent sessions you run will appear here." dark />;
    }

    return (
      <div data-test-element="list">
        {sessions.map((session) => (
          <div
            key={session.api_id}
            className={styles.row}
            role="link"
            tabIndex={0}
            onClick={() => openSession(session)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                openSession(session);
              }
            }}
            data-test-element="row"
            data-testid={`recent-session-${session.api_id}`}
          >
            <span className={`link dark-blue ${styles.rowAgent}`} data-test-element="agent-name">
              {session.agent_name}
            </span>
            <span className={styles.rowDate} data-test-element="timestamp">
              <DateTimeRenderer value={session.created_at} />
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={styles.panel}
      data-test-component="RecentSessionsPanel"
      data-test-element="container"
      data-testid="recent-sessions-panel"
    >
      <div className={styles.panelHeader} data-test-element="header">
        <span className={styles.headerTitle} data-test-element="title">
          Recent Sessions
        </span>
        <span
          className="link dark-blue pointer f6"
          onClick={onViewAllSessions}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onViewAllSessions();
            }
          }}
          data-test-element="link"
          data-testid="view-all-sessions"
        >
          View all
        </span>
      </div>
      <div className={styles.panelBody} data-test-element="body">
        {renderBody()}
      </div>
    </div>
  );
};

export default RecentSessionsPanel;
